import axios, { InternalAxiosRequestConfig } from 'axios';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { appConfig } from '../AppConfig';
import { appInsights } from '../AppInsights';
import { acquireAccessToken } from '../Security/azureAdAuthentication';
import { EnterpriseGPTApiV1ClientWrapper } from './EnterpriseGPTApiV1ClientWrapper';

// A request aborted on purpose - user stopped a completion, component unmounted, newer request superseded it -
// is not a failure and must not be reported as an exception.
export const isRequestCanceled = (error: unknown): boolean =>
    axios.isCancel(error) || (error as Error | undefined)?.name === 'CanceledError';

// a 404 is an expected outcome (deleted or unknown resource), not an application failure
export const isNotFoundError = (error: unknown): boolean =>
    axios.isAxiosError(error) && error.response?.status === 404;

// a 451 means the prompt was blocked by content moderation: a user-input outcome shown in the UI, not a malfunction
export const isContentFilterError = (error: unknown): boolean =>
    axios.isAxiosError(error) && error.response?.status === 451;

export class ServiceFactory {
    public static EnterpriseGPTApiClient(): EnterpriseGPTApiV1ClientWrapper {
        const instance = axios.create();
        instance.interceptors.request.use(async (request) => {
            await injectBearerTokenInterceptor(request);
            await injectClientVersionInterceptor(request);
            return request;
        });
        instance.interceptors.response.use(
            async (response) => {
                return response;
            },
            (error) => {
                if (isRequestCanceled(error) || isNotFoundError(error)) {
                    return Promise.reject(error);
                }
                if (isContentFilterError(error)) {
                    appInsights.trackTrace({
                        message: 'Request blocked by content moderation filter',
                        severityLevel: SeverityLevel.Warning,
                        properties: { categories: (error.response?.data?.categories ?? []).join(',') },
                    });
                    return Promise.reject(error);
                }
                appInsights.trackException({ exception: error as Error });
                if (error.name === 'Error' && error.message === 'Network Error') {
                    window.location.reload();
                }
                return Promise.reject(error);
            },
        );
        return new EnterpriseGPTApiV1ClientWrapper(appConfig.MIDDLEWARE_API_BASE_URL, instance);
    }
}

let cachedDefaultJson: any = null;

const loadDefaultJson = async () => {
    if (!cachedDefaultJson) {
        const response = await fetch('/locales/en/default.json');
        cachedDefaultJson = await response.json();
    }
    return cachedDefaultJson;
};


const injectBearerTokenInterceptor = async (request: InternalAxiosRequestConfig): Promise<void> => {
    if (request.headers) {
        const accessToken = await acquireAccessToken(appConfig.MIDDLEWARE_API_SCOPES);
        if (accessToken) {
            request.headers.Authorization = `Bearer ${accessToken}`;
        }
    }
};

// set the client version in the request header
// this is used to track the client version in the back-end middleware
const injectClientVersionInterceptor = async(request: InternalAxiosRequestConfig): Promise<void> => {
    const defaultJson = await loadDefaultJson();

    if (defaultJson.internalVersion) {
        request.headers['x-client-version'] = defaultJson.internalVersion;
    }
}